import { useEffect, useRef } from 'react';

/**
 * 15-minute idle timeout per HIPAA Security Rule + DREAM-1 AC7/AC8.
 *
 * Resets on: mousemove, keydown, click, touchstart, scroll.
 * After `idleMs` of no activity, calls `onIdle`.
 *
 * Only active when `enabled` is true (e.g. when the user is signed in).
 */
export function useIdleTimeout(onIdle: () => void, opts: { idleMs?: number; enabled?: boolean } = {}): void {
  const { idleMs = 15 * 60 * 1000, enabled = true } = opts;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      return;
    }

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(onIdle, idleMs);
    };

    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    for (const ev of events) {
      window.addEventListener(ev, reset, { passive: true });
    }
    reset();
    return () => {
      for (const ev of events) window.removeEventListener(ev, reset);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [onIdle, idleMs, enabled]);
}
